import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useIsRTL} from '@aboutyou/i18n';
import {useCallback} from 'react';
import {useLayoutSnapshot, useRect} from 'react-viewport-utils';
import {useDirectionalScrollPosition} from '../../../../hooks/useDirectionalScrollPosition/useDirectionalScrollPosition';
import {useIsomorphicLayoutEffect} from '../../../../hooks/useIsomorphicLayoutEffect/useIsomorphicLayoutEffect';
import {
  ScrollToParams,
  ScrollToSlideOptions,
  SliderHookResponse,
} from '../types';

const updateConf = {
  disableScrollUpdates: true,
  priority: 'low',
} as const;

export const useSlider = (
  sliderRef: React.RefObject<HTMLElement | null>,
  {padding = 0, ...sliderOptions}: {padding?: number; uniqueKey?: string} = {},
  firstChildRef?: React.RefObject<HTMLElement | null>,
): SliderHookResponse => {
  const isRTL = useIsRTL();
  const rect = useRect(firstChildRef ?? sliderRef, updateConf, [
    firstChildRef?.current,
    sliderRef.current,
  ]);
  const currentScrollX = useDirectionalScrollPosition(sliderRef);

  const uniqueKey =
    'uniqueKey' in sliderOptions ? sliderOptions.uniqueKey : undefined;

  const getScrollWidth = () =>
    sliderRef.current ? sliderRef.current.scrollWidth : NaN;
  const getWidth = () =>
    sliderRef.current ? sliderRef.current.getBoundingClientRect().width : 0;
  const getSlidesNumber = () =>
    sliderRef.current ? sliderRef.current.childElementCount : NaN;

  const scrollWidth =
    useLayoutSnapshot(getScrollWidth, updateConf, [sliderRef.current]) || NaN;
  const slidesNumber =
    useLayoutSnapshot(getSlidesNumber, updateConf, [sliderRef.current]) || NaN;
  const sliderViewportWidth =
    useLayoutSnapshot(getWidth, updateConf, [sliderRef.current]) || NaN;

  const horizontalPadding = padding * 2;
  const width = rect
    ? rect.width - horizontalPadding
    : getWidth() - horizontalPadding;

  const {initialScrollX} = useScrollRestoration({uniqueKey, sliderRef});
  const fallbackInitialScrollX = isNaN(initialScrollX) ? 0 : initialScrollX;
  const scrollX = isNaN(currentScrollX)
    ? fallbackInitialScrollX
    : Math.abs(currentScrollX);

  const scrollTo = useCallback(
    (direction: ScrollToParams) => {
      if (!sliderRef.current) {
        return;
      }
      const currentX = sliderRef.current.scrollLeft;
      const nextX = currentX + (direction === 'start' ? -width : width);
      if (!sliderRef.current.scrollTo) {
        sliderRef.current.scrollLeft = nextX;
      } else {
        sliderRef.current.scrollTo({
          top: 0,
          left: nextX,
          behavior: 'smooth',
        });
      }
    },
    [sliderRef, width],
  );

  const scrollToItem = useCallback(
    (direction?: 'start' | 'end') => {
      if (!sliderRef.current) {
        return;
      }

      const itemWidth = (scrollWidth - slidesNumber) / slidesNumber;

      const currentX = sliderRef.current.scrollLeft;

      const nextX =
        direction === 'start' ? currentX - itemWidth : currentX + itemWidth;

      if (!sliderRef.current.scrollTo) {
        sliderRef.current.scrollLeft = nextX;
      } else {
        sliderRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
          left: nextX,
        });
      }
    },
    [sliderRef, width, scrollWidth, slidesNumber],
  );

  const scrollToSlide = useCallback(
    (position: number, options?: ScrollToSlideOptions) => {
      if (!sliderRef.current) {
        return;
      }
      // This function might be called before rect finishes rendering in a `useLayoutEffect`. For the first mount, this would possibly result in no value, which results in UI to jump. By checking the reference directly in case the values are not defined we can avoid this.
      const currentWidth = isNaN(width) ? getWidth() : width;
      const currentScrollWidth = isNaN(scrollWidth)
        ? getScrollWidth()
        : scrollWidth;
      const currentSlidesNumber = isNaN(slidesNumber)
        ? getSlidesNumber()
        : slidesNumber;
      const sliderGap = options?.sliderGap ?? 0;
      const behavior = options?.behavior ?? 'smooth';

      const itemWidth =
        (currentScrollWidth - currentSlidesNumber * sliderGap) /
        currentSlidesNumber;
      const gapInPixelsToScroll = sliderGap * position;
      const pixelsToScroll = itemWidth * position + gapInPixelsToScroll;

      if (!sliderRef.current.scrollTo) {
        sliderRef.current.scrollLeft = pixelsToScroll;
      } else {
        if (options?.centered) {
          const scrollToCenter = pixelsToScroll - currentWidth / 3;
          sliderRef.current.scrollTo({
            top: 0,
            behavior,
            ...(isRTL ? {left: -scrollToCenter} : {left: scrollToCenter}),
          });
        } else {
          sliderRef.current.scrollTo({
            top: 0,
            behavior,
            ...(isRTL ? {left: -pixelsToScroll} : {left: pixelsToScroll}),
          });
        }
      }
    },
    [sliderRef, width, scrollWidth, slidesNumber],
  );

  const next = useCallback(() => scrollTo('end'), [scrollTo]);
  const scrollToNextItem = useCallback(
    () => scrollToItem('end'),
    [scrollToItem],
  );
  const prev = useCallback(() => scrollTo('start'), [scrollTo]);
  const scrollToPrevItem = useCallback(
    () => scrollToItem('start'),
    [scrollToItem],
  );
  const leftoverOfViewport = sliderViewportWidth - width;

  const nextIsActive = firstChildRef?.current
    ? scrollWidth >
      Math.ceil(scrollX + width + leftoverOfViewport + horizontalPadding * 2)
    : scrollWidth > Math.ceil(scrollX + width + horizontalPadding * 2);
  const prevIsActive = scrollX > 0;
  const scrollRelativeX = (scrollX + width / 2) / scrollWidth;
  const currentSlide = firstChildRef?.current
    ? Math.floor((scrollX + width + leftoverOfViewport) / width)
    : Math.floor((scrollX + width / 2) / width);

  return {
    next,
    prev,
    nextIsActive,
    prevIsActive,
    scrollRelativeX,
    slidesNumber,
    currentSlide: isNaN(currentSlide) ? 0 : currentSlide,
    scrollX,
    leftoverOfViewport: leftoverOfViewport ?? 0,
    scrollToSlide,
    scrollToNextItem,
    scrollToPrevItem,
  };
};

const useScrollRestoration = ({
  uniqueKey,
  sliderRef,
}: {
  uniqueKey?: string;
  sliderRef: React.RefObject<HTMLElement | null>;
}) => {
  // Scroll position used to restore the previous scroll position on history back navigation
  const [initialScrollX, setInitialScrollX] = useHistoryPageState<number>(NaN, {
    uniqueKey: `@ui-components/HorizontalSlider${uniqueKey}`,
    // If `uniqueKey` is not set, the same page state entry will be accessed, but the scroll position will not be used,
    // so this has no effect and it is safe to silence the warning
    // TODO: https://aboutyou.atlassian.net/browse/ASFE-7899 - Remove this once `uniqueKey` becomes mandatory
    silenceErrors: true,
  });

  // On mount set the initial scroll position based on the route state if a unique key is provided
  // This will restore the previous scroll position on history back navigation
  // `history.location.key` is used to restore the scroll position for the case when we navigate to a page
  // that has the same slider element, so the component does not unmount at all.
  //
  // On unmount save the current scroll position to route state, so that it can be used to restore the scroll position.
  useIsomorphicLayoutEffect(() => {
    let scrollPositionChanged = false;
    const onScroll = () => {
      scrollPositionChanged = true;
    };
    if (!uniqueKey) {
      return;
    }

    // Updating the scroll left position triggers Layout. It can be avoided if the scroll position was never set before, which is usually the case on forward navigation. This NaN check therefore will save a few milliseconds on every page navigation.
    if (sliderRef.current && !isNaN(initialScrollX)) {
      sliderRef.current.scrollLeft = initialScrollX;
    }

    if (sliderRef.current) {
      // if the user actually scrolls we set a variable, so that we know that we have to update the position at unmount. If the user never scrolls, we can just keep the former value as nothing should have updated. This reduces one expensive update on unmount.
      sliderRef.current.addEventListener('scroll', onScroll, {
        passive: true,
        once: true,
      });
    }

    return () => {
      if (sliderRef.current) {
        sliderRef.current.removeEventListener('scroll', onScroll);
        if (scrollPositionChanged) {
          setInitialScrollX(sliderRef.current.scrollLeft ?? NaN);
        }
      }
    };
  }, [uniqueKey, sliderRef]);

  return {initialScrollX};
};
